import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { EditorContent, useEditor } from '@tiptap/react';
import { EditorState } from '@tiptap/pm/state';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { readRichText } from '../../../../../common/richTextDocument';
import {
  EMPTY_DOCUMENT,
  LIMIT_MESSAGE,
  serializeDocument,
  richTextExtensions,
  safeLink,
  cleanPastedHTML
} from './document';
import '../../RichTextValue/styles.scss';
import './styles.scss';

const RichTextEditor = ({ name, label, value, onChange, onBlur }) => {
  const [notice, setNotice] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [href, setHref] = useState('');
  const [linkError, setLinkError] = useState('');
  const lastValue = useRef(value);
  const rejectedPaste = useRef(false);
  const rejectLargeTransfer = (_view, event) => {
    const data = event.clipboardData || event.dataTransfer;
    if (
      ['text/html', 'text/plain'].some(
        (type) => new Blob([data?.getData(type) || '']).size > 256 * 1024
      )
    ) {
      event.preventDefault();
      setNotice(LIMIT_MESSAGE);
      return true;
    }
    return false;
  };
  const callbacks = useRef({ onChange, onBlur });
  callbacks.current = { onChange, onBlur };
  const editor = useEditor({
    extensions: richTextExtensions(setNotice),
    content: readRichText(value).document || EMPTY_DOCUMENT,
    shouldRerenderOnTransaction: true,
    editorProps: {
      handleDOMEvents: {
        paste: rejectLargeTransfer,
        drop: rejectLargeTransfer
      },
      attributes: {
        role: 'textbox',
        'aria-label': label,
        'aria-multiline': 'true',
        'aria-describedby': name + '-richtext-help'
      },
      transformPastedHTML: (html) => {
        rejectedPaste.current = false;
        if (new Blob([html]).size > 256 * 1024) {
          rejectedPaste.current = true;
          setNotice(LIMIT_MESSAGE);
          return '';
        }
        const cleaned = cleanPastedHTML(html);
        if (!cleaned.trim() && html.trim()) {
          rejectedPaste.current = true;
          setNotice(
            'There is no supported text to paste. Your previous content is unchanged.'
          );
        }
        return cleaned;
      },
      handlePaste: (_view, event) => {
        if (rejectedPaste.current) {
          rejectedPaste.current = false;
          return true;
        }
        if (event.clipboardData?.files?.length) {
          setNotice('Images and file attachments are not supported.');
          return true;
        }
        return false;
      },
      handleDrop: (_view, event) => {
        if (rejectedPaste.current) {
          rejectedPaste.current = false;
          return true;
        }
        if (event.dataTransfer?.files?.length) {
          setNotice('Images and file attachments are not supported.');
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor: current }) => {
      const next = serializeDocument(current.getJSON());
      lastValue.current = next.value;
      setNotice('');
      callbacks.current.onChange({ target: { name, value: next.value } });
    },
    onBlur: () => callbacks.current.onBlur?.({ target: { name } })
  });
  useEffect(() => {
    if (!editor || value === lastValue.current) return;
    lastValue.current = value;
    // A reset/server refresh starts a new undo history; cancelled edits must not return.
    const doc = editor.schema.nodeFromJSON(
      readRichText(value).document || EMPTY_DOCUMENT
    );
    editor.view.updateState(
      EditorState.create({
        doc,
        schema: editor.schema,
        plugins: editor.state.plugins
      })
    );
    setNotice('');
  }, [editor, value]);
  if (!editor) return null;
  const action = (text, command, active = false, disabled = false) => (
    <Button
      key={text}
      type="button"
      size="small"
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={command}
    >
      {text}
    </Button>
  );
  const openLink = () => {
    setHref(editor.getAttributes('link').href || '');
    setLinkError('');
    setLinkOpen(true);
  };
  const closeLink = () => {
    setLinkOpen(false);
    editor.commands.focus();
  };
  const applyLink = () => {
    if (!safeLink(href)) {
      setLinkError(
        'Enter a full http:// or https:// address without spaces or login details.'
      );
      return;
    }
    const chain = editor.chain().focus().extendMarkRange('link');
    if (editor.state.selection.empty && !editor.isActive('link')) {
      chain
        .insertContent({
          type: 'text',
          text: href,
          marks: [{ type: 'link', attrs: { href } }]
        })
        .run();
    } else chain.setLink({ href }).run();
    setLinkOpen(false);
  };
  const count = serializeDocument(editor.getJSON()).characters;
  return (
    <div className="rich-text-editor">
      <div
        className="rich-text-toolbar"
        role="group"
        aria-label={label + ' formatting'}
      >
        {action(
          'Paragraph',
          () => editor.chain().focus().setParagraph().run(),
          editor.isActive('paragraph')
        )}
        {[1, 2].map((level) =>
          action(
            'Heading ' + level,
            () => editor.chain().focus().toggleHeading({ level }).run(),
            editor.isActive('heading', { level })
          )
        )}
        {action(
          'Bold',
          () => editor.chain().focus().toggleBold().run(),
          editor.isActive('bold')
        )}
        {action(
          'Italic',
          () => editor.chain().focus().toggleItalic().run(),
          editor.isActive('italic')
        )}
        {action(
          'Underline',
          () => editor.chain().focus().toggleUnderline().run(),
          editor.isActive('underline')
        )}
        {action(
          'Bullet list',
          () => editor.chain().focus().toggleBulletList().run(),
          editor.isActive('bulletList')
        )}
        {action(
          'Numbered list',
          () => editor.chain().focus().toggleOrderedList().run(),
          editor.isActive('orderedList')
        )}
        {action(
          'Indent list',
          () => editor.chain().focus().sinkListItem('listItem').run(),
          false,
          !editor.can().sinkListItem('listItem')
        )}
        {action(
          'Outdent list',
          () => editor.chain().focus().liftListItem('listItem').run(),
          false,
          !editor.can().liftListItem('listItem')
        )}
        {action('Link', openLink, editor.isActive('link'))}
        {action(
          'Remove link',
          () =>
            editor.chain().focus().extendMarkRange('link').unsetLink().run(),
          false,
          !editor.isActive('link')
        )}
        {action('Clear formatting', () =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        )}
        {action(
          'Undo',
          () => editor.chain().focus().undo().run(),
          false,
          !editor.can().undo()
        )}
        {action(
          'Redo',
          () => editor.chain().focus().redo().run(),
          false,
          !editor.can().redo()
        )}
      </div>
      <EditorContent editor={editor} className="rich-text-article" />
      <p id={name + '-richtext-help'} className="rich-text-editor-help">
        {count.toLocaleString()} / 20,000 characters. Paste keeps supported text
        formatting; images and attachments are not supported.
      </p>
      {notice && (
        <p className="rich-text-editor-error" role="alert">
          {notice}
        </p>
      )}
      <Dialog
        open={linkOpen}
        onClose={closeLink}
        fullWidth
        maxWidth="sm"
        aria-labelledby={name + '-link-title'}
      >
        <DialogTitle id={name + '-link-title'}>Add or edit link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Link address"
            value={href}
            onChange={(event) => {
              setHref(event.target.value);
              setLinkError('');
            }}
            error={!!linkError}
            helperText={linkError || 'https://example.com'}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={closeLink}>
            Cancel
          </Button>
          <Button type="button" onClick={applyLink}>
            Apply link
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
RichTextEditor.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func
};

const RichTextField = (props) =>
  readRichText(props.value).error ? (
    <p role="alert">
      This article cannot be edited because its saved format is invalid. Its
      original value has been preserved.
    </p>
  ) : (
    <RichTextEditor key={props.name} {...props} />
  );
RichTextField.propTypes = RichTextEditor.propTypes;
export default RichTextField;
